<script setup lang="ts">
import { onBeforeUnmount, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import BPdfViewer from "@/components/common/pdf/BPdfViewer.vue";
import type { TPdfScale } from "@/components/common/pdf/pdfjs";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    filePath: string;
    scale: TPdfScale;
    /** Download name, with or without .pdf */
    fileName?: string;
    variant?: "modal" | "inline";
  }>(),
  {
    fileName: "document",
    variant: "modal",
  },
);

// Fetched with a plain GET (same call the download button uses) and handed over as bytes. The presigned S3 URL
// is signed for GET only, so letting pdf.js probe it with its own HEAD/range request fails (403 + CORS).
const data = shallowRef<Uint8Array | null>(null);
const hasLoadError = shallowRef(false);
let lastLoad = 0;

const loadPdf = async (url: string) => {
  const load = ++lastLoad;
  data.value = null;
  hasLoadError.value = false;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const bytes = new Uint8Array(await response.arrayBuffer());
    // Another file asked for meanwhile, or the component gone
    if (load === lastLoad) data.value = bytes;
  } catch (error) {
    if (load !== lastLoad) return;
    console.error("Failed to fetch PDF", error);
    hasLoadError.value = true;
  }
};

watch(
  () => props.filePath,
  (url) => {
    if (url) loadPdf(url);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  lastLoad++;
});
</script>

<template>
  <div
    class="relative flex h-full w-full flex-col"
    :class="variant === 'inline' ? 'min-h-0' : 'min-h-[80vh]'"
  >
    <p
      v-if="hasLoadError"
      class="subtitle absolute inset-0 flex items-center justify-center p-4 text-center text-sm"
      data-cy="candidate-document-load-error"
    >
      {{ t("components.pdfViewer.loadError") }}
    </p>
    <BPdfViewer v-else class="flex-1" :data="data" :scale="scale" :file-name="fileName" />
  </div>
</template>
